import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  Chart,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  Title,
  Tooltip,
} from 'chart.js'

import { getChartStyles } from '../utils'

Chart.defaults.font.size = 18
Chart.register(
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  Title,
  Tooltip,
)

const CANVAS_WIDTH = 768
const CANVAS_HEIGHT = 768

const Canvas = styled.canvas`
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
`

const chartConfig = {
  type: 'radar',
  options: {
    responsive: false,
    elements: {
      line: {
        borderWidth: 3
      }
    },
    plugins: {
      legend: {
        display: true,
      }
    },
    scales: {
      r: {
        min: 1,
        max: 5,
        pointLabels: {
          font: {
            size: 14
          }
        }
      }
    }
  },
}

const RadarChart = ({ datasets = [], labels = [] }) => {
  const canvasRef = useRef()
  const chartRef = useRef()
  const usedColors = useRef([])

  const generateChartConfig = useCallback(() => {
    usedColors.current = []

    return {
      ...chartConfig,
      labels,
      datasets: datasets.map(dataset => ({
        ...getChartStyles(usedColors.current),
        ...dataset
      }))
    }
  }, [datasets, labels])

  useEffect(() => {
    if (!canvasRef.current) {
      return null
    }
    // https://www.chartjs.org/docs/latest/charts/radar.html
    chartRef.current = new Chart(canvasRef.current, generateChartConfig())
    chartRef.current.resize(CANVAS_WIDTH, CANVAS_HEIGHT)
    return () => chartRef.current.destroy()
  }, []) // eslint-disable-line

  useEffect(() => {
    chartRef.current.data = generateChartConfig()
    chartRef.current.update()
  }, [datasets, labels, generateChartConfig])

  return (
    <Canvas ref={canvasRef} />
  )
}

RadarChart.propTypes = {
  datasets: PropTypes.arrayOf(PropTypes.object),
  labels: PropTypes.arrayOf(PropTypes.string),
}

export default RadarChart
